<script setup lang="ts">
import DirectMessagesNavigation from "@/v2Projects/components/DirectMessagesNavigation.vue";
import { ref, computed, watch, onMounted } from "vue";
import { useRouter, useRoute } from 'vue-router';
import SideNavigation from "@/components/SideNavigation.vue";
import TopNavigation from "@/components/TopNavigation.vue";
import OverviewIcon from "@/assets/icons/overview.svg";
import Leads from "@/assets/icons/leads.svg";
import ReviewsIcon from "@/assets/icons/reviews.svg";
import SettingsIcon from "@/assets/icons/settings.svg";
import LogoutIcon from "@/assets/icons/logout.svg";
import { useAuthStore } from "@/store/auth.ts";
import { UiToaster } from "@shopexperts/shared";
import NotificationsDropdown from "@/components/client/NotificationsDropdown.vue";
import ClientProfileDropdown from "@/components/client/ClientProfileDropdown.vue";
import AnnouncementBar from "@/components/common/AnnouncementBar.vue";
import { usePostHog } from "@/app/composables/usePostHog";
import { useFeatureFlags } from "@/app/composables/useFeatureFlags";
import NotificationToastListener from "@/v2Projects/components/NotificationToastListener.vue";
import NotificationToastBody from "@/v2Projects/components/NotificationToastBody.vue";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const { isEnabled } = useFeatureFlags();

// Initialize PostHog tracking
const { trackPageView, trackEvent, identify, reset } = usePostHog();

// Track page views on route changes
watch(
  () => route.path,
  (newPath) => {
    // Get a friendly page name from the path
    const pageName = newPath.split('/').pop() || 'unknown';
    trackPageView(`client_${pageName}`, { path: newPath });
  }
);

// Identify user on mount if authenticated
onMounted(() => {
  if (authStore.user?.id) {
    identify(authStore.user.id, {
      email: authStore.user.email,
      first_name: authStore.user.first_name,
      last_name: authStore.user.last_name,
      user_type: authStore.user.type || 'client',
    });
  }
});

const navItems = computed(() => {
  if (isEnabled('enable_v2_projects')) {
    return [
      { label: 'Overview', icon: OverviewIcon, path: '/client/overview' },
      { label: 'Projects', icon: Leads, path: '/client/projects' },
      { label: 'Messages', icon: 'chat-bubble', path: '/client/messages' },
      { label: 'Reviews', icon: ReviewsIcon, path: '/client/reviews' },
    ];
  }

  return [
    { label: 'Overview', icon: OverviewIcon, path: '/client/overview' },
    { label: 'My Requests', icon: Leads, path: '/client/leads' },
    { label: 'Reviews', icon: ReviewsIcon, path: '/client/reviews' },
  ];
});

const mobileMenuItems = [
  { label: 'Settings', icon: SettingsIcon, path: '/client/settings' },
];

const isMobileMenuOpen = ref(false);

const handleLogout = async () => {
  // Track logout event before resetting identity
  trackEvent('client_logout');
  
  // Reset PostHog identity
  reset();
  
  authStore.logout();
  await router.push('/login');
};
</script>

<template>
  <UiToaster v-if="isEnabled('enable_v2_projects')" placement="top-end" top-offset="4.75rem">
    <template #toast="{ toast }">
      <NotificationToastBody :toast="toast" />
    </template>
    <NotificationToastListener />
  </UiToaster>
  <div class="flex h-screen overflow-hidden flex-col bg-muted">
      <AnnouncementBar />
      <TopNavigation v-model="isMobileMenuOpen">
        <template #items>
          <NotificationsDropdown />
        </template>
        <template #desktop-items>
          <ClientProfileDropdown />
        </template>
      </TopNavigation>

      <div class="flex flex-1 min-h-0">
        <SideNavigation
          class="shrink-0 xl:w-56 2xl:w-60"
          :menu-items="navItems"
          :mobile-menu-items="mobileMenuItems"
          :mobile-logout-icon="LogoutIcon"
          :compact="true"
          v-model:mobileMenuOpen="isMobileMenuOpen"
          @logout="handleLogout"
        >
          <template #nav-messages="{ close }">
            <DirectMessagesNavigation side="client" @navigate="close" />
          </template>
        </SideNavigation>
        <div class="flex-1 min-w-0 min-h-0 flex">
          <RouterView />
        </div>
      </div>
  </div>
</template>

<style scoped>
</style>
